import { Component, OnInit, ViewEncapsulation, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { RevisionPageDataService } from "../revision-page-data.service";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { GetSourceMetaParams } from "src/app/store/models/right-panel-models";
import { AppService } from "src/app/app.service";
import { Subscription } from "rxjs";
import { SetSourceMetaGridFilters } from "src/app/store/actions/right-panel-actions";
import dayjs from "dayjs";
import {
  VAIconRegistryService,
  arrowCircleLeft,
  arrowCircleRight,
} from "@va/core-library/icons";

@Component({
  selector: "app-revision-page",
  templateUrl: "./revision-page.component.html",
  styleUrls: ["./revision-page.component.scss"],
  host: { class: "revision-page-component" },
  encapsulation: ViewEncapsulation.None,
})
export class RevisionPageComponent implements OnInit, OnDestroy {
  revisionMeta: any = {
    filters: {
      percentChange: "default",
      startDate: "PreQ",
      filterLineItem: "",
    },
  };
  gridFilters = {
    percentChange: "default",
    startDate: "PreQ",
    filterLineItem: "",
    cut_expansions: [],
    showYoyPercent: false,
    onlyPercentChange: false,
    period_list: [],
  };
  disabledSidebar = false;
  isRightsideBarCollapsed = true;
  showRightSidebar = true;
  ticker = "";
  customDate: Date = null;
  today: Date;
  legacyUrl = `${window.caUrl}/app`;
  metaSubscriber: Subscription;
  pageParamsSubcription: Subscription;

  rightPanelLoaded: Subscription;

  constructor(
    private dataService: RevisionPageDataService,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([arrowCircleLeft, arrowCircleRight]);
  }

  ngOnInit() {
    this.pageParamsSubcription = this.store
      .select(stateGetter("navigationSettings", "pageParams", "ticker"))
      .subscribe((ticker: string) => {
        if (ticker) {
          this.ticker = ticker;
          this.getRevisionMeta();
        }
      });

    this.metaSubscriber = this.store
      .select(stateGetter("rightPanel", "sourceMeta"))
      .subscribe((data: GetSourceMetaParams) => {
        const ticker = data.ticker;
        if (ticker !== undefined) {
          this.getRevisionMeta();
        }
      });
    this.store.select(stateGetter("globalSubheader")).subscribe((resp: any) => {
      this.revisionMeta.tickerFullName = resp.subheaderTitle;
    });

    this.route.params.subscribe((params) => {
      this.titleService.setTitle(
        `Revisions Analysis - ${params.ticker.toUpperCase()}`,
      );
    });

    this.logUserAction();
  }

  ngOnDestroy() {
    if (this.metaSubscriber) {
      this.metaSubscriber.unsubscribe();
    }
    if (this.pageParamsSubcription) {
      this.pageParamsSubcription.unsubscribe();
    }
  }

  getRevisionMeta() {
    this.dataService
      .getRevisionsMata(this.ticker, null)
      .subscribe((response) => {
        if (response.status === "error") {
          this.router.navigateByUrl("/home");
          return;
        }

        if (this.rightPanelLoaded) {
          this.rightPanelLoaded.unsubscribe();
        }

        this.rightPanelLoaded = this.store
          .select(stateGetter("rightPanel", "sourceMeta", "loaded"))
          .subscribe((loaded: boolean) => {
            if (!loaded) {
              return;
            }

            const revisionMeta = response;
            this.revisionMeta = revisionMeta;
            this.gridFilters = JSON.parse(
              JSON.stringify(this.revisionMeta.filters),
            );

            if (
              !this.revisionMeta.filters.cut_expansions ||
              this.revisionMeta.filters.cut_expansions.length === 0
            ) {
              this.revisionMeta.filters.cut_expansions = [
                this.revisionMeta.tabs[0].name,
              ];
              this.gridFilters.cut_expansions = [
                this.revisionMeta.tabs[0].name,
              ];
            } else {
              this.gridFilters.cut_expansions =
                this.revisionMeta.filters.cut_expansions;
            }
            this.gridFilters.percentChange = this.gridFilters.percentChange
              ? this.gridFilters.percentChange
              : "default";
            this.gridFilters.startDate = this.gridFilters.startDate
              ? this.gridFilters.startDate.indexOf("|") > -1
                ? "customDate"
                : this.gridFilters.startDate
              : "PreQ";
            if (this.gridFilters.startDate === "customDate") {
              this.customDate = new Date(
                this.revisionMeta.filters.startDate.split("|").pop(),
              );
            }
            this.gridFilters.filterLineItem = this.gridFilters.filterLineItem
              ? this.gridFilters.filterLineItem
              : "";

            this.gridFilters.showYoyPercent = this.gridFilters.showYoyPercent
              ? this.gridFilters.showYoyPercent
              : false;
            this.gridFilters.onlyPercentChange = this.gridFilters
              .onlyPercentChange
              ? this.gridFilters.onlyPercentChange
              : false;

            this.gridFilters.period_list =
              this.gridFilters.period_list || this.revisionMeta.periods;
          });
      });
  }

  onClearLineitemSearch(revisionGrid) {
    this.gridFilters.filterLineItem = "";
    revisionGrid.onFilterChange(["lineitem"]);
  }

  onStartDateChange(revisionGrid) {
    if (this.gridFilters.startDate === "customDate") {
      this.customDate = null;
    }
    revisionGrid.startDateChange();
  }

  onFilterChange(isSwitchOn: boolean, property: string) {
    this.gridFilters[property] = isSwitchOn;
    if (property === "onlyPercentChange") {
      // this.gridFilters.onlyPercentChange = false;

      //save user preference
      const jsonData = { filters: this.gridFilters };
      this.dataService.saveUserFilters(jsonData, this.ticker).subscribe();

      //logging
      this.appService.logUserAction(
        {},
        this.ticker,
        null,
        "Show only percent change",
        { beginning_state_referrer: "Revision Analysis Page" },
        "",
        "Revision Toggle click",
      );
    } else if (property === "showYoyPercent" && isSwitchOn === true) {
      this.gridFilters.onlyPercentChange = false;
    }

    // same object returns true, not same then false
    // true -> Don;t set clear Flag
    // false -> set clear Flag
    const diffInFilter = this.appService.compare(
      this.revisionMeta.filters,
      this.gridFilters,
    );
    this.store.dispatch(new SetSourceMetaGridFilters(!diffInFilter));

    this.gridFilters = Object.assign({}, this.gridFilters);
  }

  disabledDate = (current: Date): boolean => {
    this.today = new Date();
    const currentDate = dayjs(current);
    const effectiveStart = dayjs(new Date(this.revisionMeta.effective_start));
    return (
      Number(currentDate.diff(this.today, "day")) > 0 ||
      Number(effectiveStart.diff(current, "day")) > 0
    );
  };

  logUserAction() {
    const today = new Date();
    const currentTime = today.toUTCString();
    const currentURL = window.location.href;
    this.appService.logUserAction(
      {},
      this.ticker,
      "",
      "Revision Analysis Page",
      {
        PAGE: "Revision Analysis Page",
        UTCTIME: currentTime,
        URL: currentURL,
        VALUE: this.ticker,
        beginning_state_referrer: "Company page",
      },
      "",
      "Left panel click",
    );
  }

  rightSiderCollapseChanged(isCollapsed) {
    this.logRpExpansionState(isCollapsed);
  }

  logRpExpansionState(isCollapsed) {
    this.appService.logUserAction(
      `RIGHT_PANEL_${isCollapsed ? "COLLAPSE" : "EXPAND"}`,
      this.ticker,
      "",
      `RIGHT_PANEL_${isCollapsed ? "COLLAPSE" : "EXPAND"}`,
      {
        company: this.ticker,
      },
      "",
      "RIGHT_PANEL_CLICK",
    );
  }
}
